/**
 * Boxes 6, 7, 8 and 10 (R11): the /looker app-box loaders, run for one
 * workspace and the page's period, each box failing on its own. Box 6's run
 * key carries the currency (DEC5 R7) and waits until the currency is known
 * (undefined), and fails with the facts ('failed', R16); the others do not
 * reload on a currency change. All workspaces:
 * every box asks for a workspace (as the Looker D1 did) and nothing is read.
 *
 * @module hooks/useOverviewAppBoxes
 */
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { APP_WIDGETS, type AppWidget } from '@/services/looker/lookerAppTiles';
import type { DateRange } from '@/services/looker/lookerDateRange';
import type { AppLoaderContext } from '@/services/looker/lookerAppLoaders';
import { settleWidgets } from '@/services/overview/overviewLoaders';
import type { ReadState } from '@/services/overview/overviewTypes';
import type { AppTileState } from './useLookerAppTiles';
import { useKeyedRead } from './useKeyedRead';

const OTHERS: readonly AppWidget[] = ['attention', 'dataQuality', 'outcomes'];
const PACING: readonly AppWidget[] = ['budgetPacing'];

type Settled = Partial<Record<AppWidget, AppTileState>>;

const stateOf = (r: ReadState<Settled>, w: AppWidget): AppTileState => {
  if (r.status === 'ready') return r.data[w] ?? { status: 'failed' };
  return r.status === 'failed' ? { status: 'failed' } : { status: 'loading' };
};

export const useOverviewAppBoxes = (
  workspaceId: string | null,
  period: DateRange | null,
  /** The page currency; undefined = not known yet (facts loading); 'failed' = the facts failed (R16). */
  currency: string | null | undefined | 'failed',
): Record<AppWidget, AppTileState> => {
  const { t, i18n } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const base = workspaceId && period ? `${workspaceId}|${period.start}..${period.end}|${i18n.language}` : '';
  const known = currency === 'failed' ? null : currency ?? null;
  const ctx = (): AppLoaderContext => ({ workspaceId: workspaceId ?? '', period, t: tRef.current, now: new Date(), currency: known });
  const others = useKeyedRead<Settled>(base && `app|${base}`, () => settleWidgets(OTHERS, ctx()), 'OverviewAppBoxes');
  const pacing = useKeyedRead<Settled>(base && currency !== undefined && currency !== 'failed' ? `pacing|${base}|cur:${currency ?? ''}` : '',
    () => settleWidgets(PACING, ctx()), 'OverviewBudgetPacing');

  return useMemo(() => {
    if (!workspaceId) {
      return Object.fromEntries(APP_WIDGETS.map((w) => [w, { status: 'needsWorkspace' }])) as Record<AppWidget, AppTileState>;
    }
    return {
      attention: stateOf(others, 'attention'),
      dataQuality: stateOf(others, 'dataQuality'),
      outcomes: stateOf(others, 'outcomes'),
      // R16: box 6 is judged in the page currency, which only the facts know.
      // Idle (currency not known yet) reads as loading.
      budgetPacing: currency === 'failed' ? { status: 'failed' } : stateOf(pacing, 'budgetPacing'),
    };
  }, [workspaceId, others, pacing, currency]);
};
